import { useState, useEffect, useRef } from 'react';
import { useAppStore } from '../lib/store';
import type { ThemePreset, WorkType } from '../lib/store';

// ─── Data ─────────────────────────────────────────────────────────────────────

const WRITER_TYPES = [
  { id: 'fiction', label: 'Fiction', description: 'Novels, short stories, speculative worlds', glyph: '✦' },
  { id: 'nonfiction', label: 'Non-fiction', description: 'Memoir, essays, journalism, biography', glyph: '◈' },
  { id: 'poetry', label: 'Poetry', description: 'Verse, lyric essays, prose poetry', glyph: '◇' },
  { id: 'screenwriting', label: 'Screenwriting', description: 'Film, TV, stage, and new media', glyph: '▷' },
  { id: 'journal', label: 'Journal', description: 'Personal reflection, morning pages', glyph: '○' },
];

const THEMES = [
  {
    id: 'cream',
    label: 'Cream',
    bg: '#F5F1E8',
    paper: '#FAF7F0',
    text: '#2C2416',
    accent: '#8B7355',
    border: 'rgba(44,36,22,0.12)',
    description: 'Warm, like afternoon light through old windows',
  },
  {
    id: 'sepia',
    label: 'Sepia',
    bg: '#E8DCC4',
    paper: '#F0E6D2',
    text: '#3D3221',
    accent: '#6B5B45',
    border: 'rgba(61,50,33,0.15)',
    description: 'Aged paper and worn leather',
  },
  {
    id: 'moonlit',
    label: 'Moonlit',
    bg: '#1A1A2E',
    paper: '#252542',
    text: '#E8E8F0',
    accent: '#7B8CDE',
    border: 'rgba(232,232,240,0.1)',
    description: 'Deep blue nights and quiet focus',
  },
  {
    id: 'paperwhite',
    label: 'Paperwhite',
    bg: '#FFFFFF',
    paper: '#FAFAFA',
    text: '#1A1A1A',
    accent: '#4A5568',
    border: 'rgba(0,0,0,0.08)',
    description: 'Clean, bright, distraction-free',
  },
  {
    id: 'forest',
    label: 'Forest',
    bg: '#1C2621',
    paper: '#243029',
    text: '#E0E8E3',
    accent: '#7A9E7E',
    border: 'rgba(224,232,227,0.1)',
    description: 'Deep greens and the hush of old trees',
  },
  {
    id: 'midnight',
    label: 'Midnight',
    bg: '#0D1117',
    paper: '#161B22',
    text: '#C9D1D9',
    accent: '#58A6FF',
    border: 'rgba(201,209,217,0.1)',
    description: 'Code-dark, sharp, and precise',
  },
];

const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;0,600;1,400;1,500&family=DM+Sans:wght@300;400;500&display=swap');
  .ob-root { min-height:100vh; display:flex; flex-direction:column; align-items:center; justify-content:center; position:relative; overflow:hidden; transition:background 0.65s cubic-bezier(0.4,0,0.2,1); padding:1.5rem 1rem; font-family:'DM Sans',sans-serif; }
  @media (prefers-reduced-motion: reduce) {
    .ob-root, .ob-card, .ob-swatch, .ob-btn { transition: none !important; }
    .ob-spinner { animation: none !important; }
    .fade-up { animation: none !important; }
  }
  .ob-grain { position:fixed; inset:0; pointer-events:none; z-index:0; opacity:0.035; background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E"); background-size:11.25rem; }
  .ob-progress { position:fixed; top:1.75rem; left:50%; transform:translateX(-50%); display:flex; gap:0.5rem; z-index:var(--z-sticky); }
  .ob-dot { width:0.375rem; height:0.375rem; border-radius:50%; transition:all 0.35s ease; }
  .ob-dot-active { width:1.375rem !important; border-radius:0.1875rem !important; }
  .ob-dot-done { opacity:0.5 !important; }
  .ob-card { position:relative; z-index:1; border-radius:1.25rem; padding:3rem 3.25rem; width:100%; max-width:35rem; box-shadow:0 2px 1px rgba(0,0,0,0.02),0 0.5rem 1.5rem rgba(0,0,0,0.07),0 2rem 3rem rgba(0,0,0,0.04); transition:background 0.65s ease,border-color 0.65s ease,opacity 0.22s ease,transform 0.26s cubic-bezier(0.34,1.4,0.64,1); }
  .ob-eyebrow { font-size:0.6875rem; font-weight:500; letter-spacing:0.12em; text-transform:uppercase; margin-bottom:0.75rem; opacity:0.8; }
  .ob-title { font-family:'Lora',Georgia,serif; font-size:1.75rem; font-weight:500; line-height:1.25; margin-bottom:0.625rem; }
  .ob-sub { font-size:0.875rem; opacity:0.65; line-height:1.65; margin-bottom:2rem; }
  .ob-theme-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:0.75rem; margin-bottom:0.875rem; }
  .ob-swatch { display:flex; flex-direction:column; align-items:center; gap:0.4375rem; background:transparent; border:none; cursor:pointer; padding:0.25rem; border-radius:0.625rem; transition:transform 0.15s; }
  .ob-swatch:hover { transform:translateY(-2px); }
  .ob-swatch-preview { width:100%; aspect-ratio:4/3; border-radius:0.5rem; display:flex; align-items:center; justify-content:center; transition:background 0.5s ease; overflow:hidden; }
  .ob-swatch-paper { width:68%; height:72%; border-radius:0.25rem; border:1px solid; display:flex; flex-direction:column; justify-content:center; gap:0.3125rem; padding:0.4375rem 0.5625rem; }
  .ob-swatch-line { height:2px; border-radius:2px; width:100%; }
  .ob-theme-desc { font-family:'Lora',Georgia,serif; font-style:italic; font-size:0.8125rem; opacity:0.6; text-align:center; margin-bottom:1.75rem; min-height:1.125rem; transition:all 0.25s ease; }
  .ob-field { margin-bottom:1.25rem; }
  .ob-label { font-size:0.6875rem; font-weight:500; letter-spacing:0.08em; text-transform:uppercase; opacity:0.65; margin-bottom:0.4375rem; display:block; }
  .ob-select { width:100%; padding:0.75rem 2.25rem 0.75rem 0.875rem; border-radius:0.625rem; font-family:'DM Sans',sans-serif; font-size:0.875rem; outline:none; cursor:pointer; appearance:none; background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='7' viewBox='0 0 11 7'%3E%3Cpath d='M1 1l4.5 4.5L10 1' stroke='%238B7355' stroke-width='1.4' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right 0.8125rem center; padding-right:2.25rem; transition:border-color 0.2s,box-shadow 0.2s,background-color 0.65s ease; }
  .ob-select:focus { box-shadow:0 0 0 3px rgba(139,115,85,0.12); }
  .ob-input-wrap { position:relative; }
  .ob-title-input { width:100%; padding:0.875rem 1.125rem; border-radius:0.625rem; font-family:'Lora',Georgia,serif; font-size:1.1875rem; outline:none; transition:border-color 0.2s,box-shadow 0.2s,background 0.65s ease; display:block; }
  .ob-title-input::placeholder { opacity:0.5; font-style:italic; }
  .ob-hint { font-size:0.6875rem; opacity:0.5; margin-top:0.4375rem; }
  .ob-btn-row { display:flex; gap:0.625rem; justify-content:flex-end; align-items:center; margin-top:1.5rem; }
  .ob-btn { padding:0.6875rem 1.375rem; border-radius:0.625rem; font-family:'DM Sans',sans-serif; font-size:0.875rem; font-weight:500; cursor:pointer; border:none; transition:all 0.18s ease; display:flex; align-items:center; gap:0.375rem; min-height:2.75rem; }
  .ob-btn-ghost { background:transparent; opacity:0.8; }
  .ob-btn-ghost:hover { opacity:1 !important; }
  .ob-btn-primary { box-shadow:0 2px 0.5rem rgba(0,0,0,0.14); }
  .ob-btn-primary:hover { filter:brightness(1.1); transform:translateY(-1px); box-shadow:0 0.25rem 1rem rgba(0,0,0,0.18); }
  .ob-btn-primary:active { transform:none; }
  .ob-btn-disabled { opacity:0.5 !important; cursor:not-allowed; transform:none !important; filter:none !important; }
  .ob-spinner { width:0.9375rem; height:0.9375rem; border:2px solid rgba(255,255,255,0.25); border-top-color:#FAF7F0; border-radius:50%; animation:spin 0.7s linear infinite; display:inline-block; }
  .ob-done { text-align:center; padding:0.75rem 0; }
  .ob-done-glyph { font-size:2rem; margin-bottom:1.125rem; opacity:0.65; }
  .ob-done-title { font-family:'Lora',Georgia,serif; font-size:1.625rem; font-weight:500; margin-bottom:0.625rem; }
  .ob-done-sub { font-size:0.875rem; opacity:0.6; line-height:1.65; }
  .ob-wordmark { position:fixed; bottom:1.5rem; left:50%; transform:translateX(-50%); font-family:'Lora',Georgia,serif; font-style:italic; font-size:0.75rem; opacity:0.25; z-index:1; white-space:nowrap; letter-spacing:0.04em; transition:color 0.65s ease; }
  @keyframes spin { to { transform:rotate(360deg); } }
  @keyframes fadeUp { from { opacity:0; transform:translateY(0.625rem); } to { opacity:1; transform:translateY(0); } }
  .fade-up { animation:fadeUp 0.32s ease both; }
  @media (prefers-reduced-motion: reduce) {
    .fade-up { animation: none !important; }
  }
  @media (max-width:36.25rem) { .ob-card { padding:2rem 1.5rem; } .ob-title { font-size:1.375rem; } .ob-theme-grid { grid-template-columns:repeat(2,1fr); } }
`;

export default function Onboarding() {
  const [step, setStep] = useState(1);
  const [selectedTheme, setSelectedTheme] = useState<ThemePreset>('cream');
  const [workType, setWorkType] = useState<WorkType>('fiction');
  const [novelTitle, setNovelTitle] = useState('');
  const [cardVisible, setCardVisible] = useState(true);
  const [finishing, setFinishing] = useState(false);
  const [done, setDone] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const theme = THEMES.find(t => t.id === selectedTheme) ?? THEMES[0];

  const { completeOnboarding, createNovel, setThemeProfile, setActiveNovel, setViewMode } = useAppStore();

  useEffect(() => {
    if (step === 2) setTimeout(() => inputRef.current?.focus(), 380);
  }, [step]);

  // Apply theme to body background during onboarding
  useEffect(() => {
    document.body.style.backgroundColor = theme.bg;
    return () => { document.body.style.backgroundColor = ''; };
  }, [theme.bg]);

  const goTo = (next: number) => {
    setCardVisible(false);
    setTimeout(() => { setStep(next); setCardVisible(true); }, 230);
  };

  const handleFinish = () => {
    if (!novelTitle.trim()) return;
    setFinishing(true);

    // Convert ThemePreset to a ThemeProfile
    setThemeProfile({ preset: selectedTheme, background: theme.bg });

    const id = createNovel(novelTitle.trim(), '', workType);
    setActiveNovel(id);
    setViewMode('manuscript');
    completeOnboarding();

    setTimeout(() => { setDone(true); setFinishing(false); }, 900);
  };

  const placeholders: Record<WorkType, string> = {
    fiction: "The name of your novel…",
    nonfiction: "Working title…",
    poetry: "Name of this collection…",
    screenwriting: "Project title…",
    journal: "My Journal",
  };

  const canFinish = novelTitle.trim().length > 0;

  return (
    <>
      <style>{GLOBAL_STYLES}</style>
      <div className="ob-root" style={{ background: theme.bg }}>
        <div className="ob-grain" />

        <svg style={{ position: "fixed", top: -90, right: -90, width: 300, height: 300, pointerEvents: "none", zIndex: 0 }} viewBox="0 0 200 200" fill="none">
          <circle cx="100" cy="100" r="88" stroke={theme.accent} strokeWidth="1" strokeDasharray="4 9" opacity="0.14" />
          <circle cx="100" cy="100" r="52" stroke={theme.accent} strokeWidth="0.5" opacity="0.08" />
        </svg>

        {!done && (
          <div className="ob-progress">
            {[1, 2].map(n => (
              <div key={n} className={`ob-dot ${step === n ? "ob-dot-active" : ""} ${step > n ? "ob-dot-done" : ""}`}
                style={{ background: step >= n ? theme.accent : theme.border }} />
            ))}
          </div>
        )}

        <div className="ob-card" style={{
          background: theme.paper, border: `1px solid ${theme.border}`, color: theme.text,
          opacity: cardVisible ? 1 : 0, transform: cardVisible ? "translateY(0)" : "translateY(10px)",
        }}>
          {done ? (
            <div className="ob-done fade-up">
              <div className="ob-done-glyph" style={{ color: theme.accent }}>✦</div>
              <div className="ob-done-title" style={{ color: theme.text }}>Your study is ready.</div>
              <p className="ob-done-sub" style={{ color: theme.text }}>"{novelTitle}" is waiting for you.</p>
            </div>

          ) : step === 1 ? (
            <div className="fade-up" key="step1">
              <div className="ob-eyebrow" style={{ color: theme.accent }}>Your space</div>
              <h1 className="ob-title" style={{ color: theme.text }}>Choose your atmosphere</h1>
              <p className="ob-sub" style={{ color: theme.text }}>The room shifts around you as you choose. Pick the light you write best in.</p>

              <div className="ob-theme-grid">
                {THEMES.map(t => (
                  <button key={t.id} className="ob-swatch" onClick={() => setSelectedTheme(t.id as ThemePreset)} title={t.label}
                    style={{ outline: selectedTheme === t.id ? `2px solid ${t.accent}` : "2px solid transparent", outlineOffset: 2, borderRadius: 10 }}>
                    <div className="ob-swatch-preview" style={{ background: t.bg }}>
                      <div className="ob-swatch-paper" style={{ background: t.paper, borderColor: t.border }}>
                        <div className="ob-swatch-line" style={{ background: t.text, opacity: 0.65, height: 3, width: "55%" }} />
                        <div className="ob-swatch-line" style={{ background: t.text, opacity: 0.22 }} />
                        <div className="ob-swatch-line" style={{ background: t.text, opacity: 0.22, width: "72%" }} />
                      </div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 500, color: theme.text, fontFamily: "'DM Sans',sans-serif" }}>{t.label}</span>
                  </button>
                ))}
              </div>

              <p className="ob-theme-desc" style={{ color: theme.text }}>{theme.description}</p>

              <div className="ob-btn-row">
                <button className="ob-btn ob-btn-primary" style={{ background: theme.accent, color: "#FAF7F0" }} onClick={() => goTo(2)}>
                  This feels right →
                </button>
              </div>
            </div>

          ) : (
            <div className="fade-up" key="step2">
              <div className="ob-eyebrow" style={{ color: theme.accent }}>Almost there</div>
              <h1 className="ob-title" style={{ color: theme.text }}>Name your first project</h1>
              <p className="ob-sub" style={{ color: theme.text }}>Every great work starts somewhere. You can change all of this anytime.</p>

              <div className="ob-field">
                <label className="ob-label" style={{ color: theme.text }}>Type of work</label>
                <select className="ob-select" value={workType} onChange={e => setWorkType(e.target.value as WorkType)}
                  style={{ background: theme.bg, color: theme.text, border: `1px solid ${theme.border}` }}>
                  {WRITER_TYPES.map(w => (
                    <option key={w.id} value={w.id}>{w.glyph}  {w.label} — {w.description}</option>
                  ))}
                </select>
              </div>

              <div className="ob-field">
                <label className="ob-label" style={{ color: theme.text }}>Title</label>
                <div className="ob-input-wrap">
                  <input ref={inputRef} className="ob-title-input" type="text" value={novelTitle}
                    onChange={e => setNovelTitle(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && canFinish) handleFinish(); }}
                    placeholder={placeholders[workType] ?? "Working title…"}
                    maxLength={120} autoComplete="off" spellCheck={false}
                    style={{ background: theme.bg, color: theme.text, border: `1px solid \${novelTitle ? theme.accent : theme.border}`, boxShadow: novelTitle ? `0 0 0 3px \${theme.accent}18` : "none" }} />
                  <p className="ob-hint" style={{ color: theme.text }}>Press Enter or click below</p>
                </div>
              </div>

              <div className="ob-btn-row">
                <button className="ob-btn ob-btn-ghost" style={{ color: theme.text, border: `1px solid ${theme.border}`, opacity: 0.5 }} onClick={() => goTo(1)}>← Back</button>
                <button className={`ob-btn ob-btn-primary \${!canFinish ? "ob-btn-disabled" : ""}`}
                  style={{ background: theme.accent, color: "#FAF7F0", minWidth: 158, justifyContent: "center" }}
                  onClick={handleFinish} disabled={!canFinish || finishing}>
                  {finishing ? <span className="ob-spinner" /> : "Open the study →"}
                </button >
              </div >
            </div >
          )
          }
        </div >

        <div className="ob-wordmark" style={{ color: theme.text }}>Write Library</div>
      </div >
    </>
  );
}
